<template>
    <div>
        <div class="w-full" id="scrollContainer">
            <nav v-if="visibleSubmenus.length" class="relative border-b border-gray-300">
                <div ref="menuContainer" class="flex items-center justify-between gap-4">
                    <div class="flex min-w-0 items-center gap-4">
                        <PageLink v-for="(submenu,index) in displayedSubmenus" :key="index" :submenu="submenu"/>
                    </div>

                    <Dropdown v-if="overflowSubmenus.length" align="right" width="48">
                        <template #trigger>
                            <button
                                type="button"
                                class="inline-flex items-center px-2 py-2 font-semibold text-xs uppercase tracking-widest focus:outline-none focus:shadow-outline-gray transition ease-in-out duration-700 whitespace-nowrap"
                                :class="{'text-indigo-500': overflowSubmenus.some(submenu => submenu.active), 'text-gray-800 hover:text-indigo-500': !overflowSubmenus.some(submenu => submenu.active)}"
                            >
                                More
                                <svg class="ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                                     fill="currentColor">
                                    <path fill-rule="evenodd"
                                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                          clip-rule="evenodd"/>
                                </svg>
                            </button>
                            <hr class="border-t-4 border-gray-800 rounded-t-md"
                                :class="{'block': overflowSubmenus.some(submenu => submenu.active), 'hidden': !overflowSubmenus.some(submenu => submenu.active)}"/>
                        </template>
                        <template #content>
                            <Link
                                v-for="(submenu,index) in overflowSubmenus"
                                :key="index"
                                :href="submenu.route"
                                class="block w-full px-4 py-2 text-sm leading-5 text-left transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                                :class="{'text-indigo-500': submenu.active, 'text-gray-700': !submenu.active}"
                            >
                                {{ submenu.title }}
                            </Link>
                        </template>
                    </Dropdown>
                </div>

                <div class="absolute invisible h-0 overflow-hidden pointer-events-none whitespace-nowrap">
                    <div class="inline-flex gap-4">
                        <div v-for="(submenu,index) in visibleSubmenus" :key="index" ref="measureItems">
                            <span
                                class="inline-flex items-center px-2 py-2 font-semibold text-xs uppercase tracking-widest whitespace-nowrap">
                                {{ submenu.title }}
                            </span>
                        </div>
                    </div>
                    <button ref="moreMeasure" type="button" class="inline-flex items-center px-2 py-2 font-semibold text-xs uppercase tracking-widest whitespace-nowrap">
                        More
                    </button>
                </div>
            </nav>
        </div>
        <div class="py-4 relative">
            <slot/>
        </div>
    </div>
</template>
<script setup>
import {computed, nextTick, onMounted, onUnmounted, ref, watch} from "vue";
import {Link} from "@inertiajs/inertia-vue3";
import Dropdown from "@/Components/Dropdown.vue";
import PageLink from "@/Components/PageLink.vue";

const props = defineProps({
    submenus: [Object, Array]
});

const menuContainer = ref(null);
const measureItems = ref([]);
const moreMeasure = ref(null);
const visibleCount = ref(0);

const visibleSubmenus = computed(() => {
    return (props.submenus ?? []).filter((submenu) => submenu.visible);
});

const displayedSubmenus = computed(() => visibleSubmenus.value.slice(0, visibleCount.value));
const overflowSubmenus = computed(() => visibleSubmenus.value.slice(visibleCount.value));

watch(visibleSubmenus, () => updateVisibleMenus(), {deep: true});

onMounted(() => {
    updateVisibleMenus();
    window.addEventListener('resize', updateVisibleMenus);
});

onUnmounted(() => {
    window.removeEventListener('resize', updateVisibleMenus);
});

async function updateVisibleMenus() {
    await nextTick();

    const availableWidth = menuContainer.value?.clientWidth ?? 0;
    const itemWidths = measureItems.value.map((item) => item?.offsetWidth ?? 0);

    if (! availableWidth || ! itemWidths.length) {
        visibleCount.value = visibleSubmenus.value.length;

        return;
    }

    const gap = 16;
    const allItemsWidth = itemWidths.reduce((total, width) => total + width, 0) + (gap * Math.max(itemWidths.length - 1, 0));

    if (allItemsWidth <= availableWidth) {
        visibleCount.value = visibleSubmenus.value.length;

        return;
    }

    const moreWidth = (moreMeasure.value?.offsetWidth ?? 56) + gap;
    const availableForItems = Math.max(availableWidth - moreWidth, 0);
    let usedWidth = 0;
    let count = 0;

    for (const width of itemWidths) {
        const nextWidth = usedWidth + width + (count > 0 ? gap : 0);

        if (nextWidth > availableForItems) {
            break;
        }

        usedWidth = nextWidth;
        count += 1;
    }

    visibleCount.value = count;
}
</script>
